import React, {
  FC, useState, useRef, useEffect,
} from 'react'
import { ChevronSVGIcon } from '@/components/icons'

// Popular Google Fonts for professional applications
export const GOOGLE_FONTS = [
  { name: 'Inter', category: 'sans-serif' },
  { name: 'Roboto', category: 'sans-serif' },
  { name: 'Open Sans', category: 'sans-serif' },
  { name: 'Lato', category: 'sans-serif' },
  { name: 'Montserrat', category: 'sans-serif' },
  { name: 'Poppins', category: 'sans-serif' },
  { name: 'Source Sans Pro', category: 'sans-serif' },
  { name: 'Nunito', category: 'sans-serif' },
  { name: 'Raleway', category: 'sans-serif' },
  { name: 'Work Sans', category: 'sans-serif' },
  { name: 'DM Sans', category: 'sans-serif' },
  { name: 'Plus Jakarta Sans', category: 'sans-serif' },
  { name: 'Manrope', category: 'sans-serif' },
  { name: 'Outfit', category: 'sans-serif' },
  { name: 'Space Grotesk', category: 'sans-serif' },
  { name: 'IBM Plex Sans', category: 'sans-serif' },
  { name: 'Barlow', category: 'sans-serif' },
  { name: 'Rubik', category: 'sans-serif' },
  { name: 'Mulish', category: 'sans-serif' },
  { name: 'Quicksand', category: 'sans-serif' },
  { name: 'Playfair Display', category: 'serif' },
  { name: 'Merriweather', category: 'serif' },
  { name: 'Lora', category: 'serif' },
  { name: 'PT Serif', category: 'serif' },
  { name: 'Source Serif Pro', category: 'serif' },
  { name: 'Roboto Mono', category: 'monospace' },
  { name: 'Fira Code', category: 'monospace' },
  { name: 'JetBrains Mono', category: 'monospace' },
]

type FontPickerProps = {
  label?: string;
  value: string | null;
  onChange: (font: string) => void;
  disabled?: boolean;
  placeholder?: string;
};

const fontLoads = new Map<string, Promise<void>>()

/**
 * Adds a Google Font's stylesheet to the page once, so text styled with that family renders in it.
 * @param fontName - Google Font family name, e.g. "Playfair Display"
 * @returns resolves once the font is ready to draw, or once it has failed to load
 */
export const loadGoogleFont = (fontName: string): Promise<void> => {
  const fontId = `google-font-${fontName.replace(/\s+/g, '-').toLowerCase()}`
  if (!fontLoads.has(fontId) || !document.getElementById(fontId)) {
    fontLoads.set(fontId, new Promise(resolve => {
      const link = document.createElement('link')
      link.id = fontId
      link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}&display=swap`
      link.rel = 'stylesheet'
      // The stylesheet only declares the font: its file is fetched once something asks for it.
      link.onload = () => Promise.resolve(document.fonts?.load(`1em "${fontName}"`)).then(() => resolve(), () => resolve())
      link.onerror = () => resolve()
      document.head.appendChild(link)
    }))
  }
  return fontLoads.get(fontId) as Promise<void>
}

// Load Google Fonts only when the picker needs to render font previews.
export const loadAllFonts = () => {
  GOOGLE_FONTS.forEach(font => loadGoogleFont(font.name))
}

const FontPicker: FC<FontPickerProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select a font...',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Keep the empty state as a plain placeholder; load font previews only when needed.
  useEffect(() => {
    if (isOpen) {
      loadAllFonts()
      return
    }

    if (value) {
      loadGoogleFont(value)
    }
  }, [isOpen, value])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (fontName: string) => {
    onChange(fontName)
    setIsOpen(false)
  }

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const selectedFont = GOOGLE_FONTS.find(f => f.name === value)

  return (
    <div className="font-picker-container relative flex flex-col gap-2" ref={containerRef}>
      {label && <h6 className="m-0 font-[Switzer-Medium] text-[14px] text-gray-1">{label}</h6>}
      <div
        className={`font-picker-select flex h-12 cursor-pointer items-center justify-between rounded-xl border border-gray-5 bg-gray-7 px-4 py-0 [transition:border-color_0.2s_ease] focus:border-gray-3 focus:[outline:none] [&.open]:border-gray-3 [&:hover:not(.disabled)]:border-gray-3 [&.disabled]:cursor-not-allowed [&.disabled]:opacity-60 ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleOpen}
        onKeyDown={e => e.key === 'Enter' && handleOpen()}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="font-picker-list"
        tabIndex={disabled ? -1 : 0}
      >
        {value ? (
          <span className="selected-font text-[14px] text-gray-1" style={{ fontFamily: value }}>
            {value}
            {selectedFont && (
              <span className="font-category ml-2 text-[12px] text-gray-3">
                (
                {selectedFont.category}
                )
              </span>
            )}
          </span>
        ) : (
          <span className="placeholder font-[Switzer] text-[14px] text-gray-3 [background:transparent]">{placeholder}</span>
        )}
        <ChevronSVGIcon className={`chevron-svg size-4 [transition:transform_0.2s_ease] [&.open]:[transform:rotate(180deg)] ${isOpen ? 'open' : ''}`} />
      </div>

      {isOpen && (
        <div className="font-picker-dropdown absolute left-0 right-0 top-[calc(100%+4px)] z-[1000] flex max-h-[300px] flex-col overflow-hidden rounded-xl border border-gray-5 bg-gray-7 [box-shadow:0px_4px_20px_0px_rgba(0,0,0,0.1)]">
          <div className="font-list max-h-[280px] overflow-y-auto bg-gray-7" id="font-picker-list">
            {GOOGLE_FONTS.map(font => (
              <div
                key={font.name}
                // The 0-4-0 `.selected` fill beats the 0-2-0 `hover:` fill; `:not(:focus)` lets the
                // `focus:` fill win over it.
                className={`font-option relative flex min-h-10 cursor-pointer items-center border-t border-t-gray-5 bg-gray-7 [transition:background-color_0.15s_ease] first:[border-top:none] hover:bg-[rgba(0,0,0,0.03)] focus:bg-[rgba(0,0,0,0.03)] focus:[outline:none] [&.font-option.selected:not(:focus)]:bg-[rgba(0,0,0,0.05)] ${value === font.name ? 'selected' : ''}`}
                onClick={() => handleSelect(font.name)}
                onKeyDown={e => e.key === 'Enter' && handleSelect(font.name)}
                role="option"
                aria-selected={value === font.name}
                tabIndex={0}
                style={{ fontFamily: font.name }}
              >
                <span className="font-name flex w-full flex-1 items-center px-3 py-[11px] text-[14px] leading-[18px] text-gray-1">{font.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default FontPicker
